import { cn } from '@/lib/cn';
import { useAuthedImage } from '@/hooks/useAuthedImage';

interface Props {
  /** Direct URL (e.g. blob:, https://) — used as-is. */
  src?: string | null;
  /**
   * API-relative path that requires auth (e.g. "/admin/images/profile-pic/42.jpg").
   * The image is fetched via the authenticated API client and rendered as a blob URL.
   */
  apiPath?: string | null;
  name?: string;
  size?: number;
  className?: string;
}

function isUsableSrc(s: string | null | undefined): boolean {
  if (!s) return false;
  const t = s.trim();
  if (!t) return false;
  // Bare filenames stored by the backend (e.g. "42.jpg") aren't loadable directly.
  return /^(https?:|data:|blob:|\/)/i.test(t);
}

function initialsFor(name: string | undefined | null): string {
  return (
    (name ?? '?')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join('') || '?'
  );
}

export function Avatar({ src, apiPath, name, size = 40, className }: Props) {
  const authed = useAuthedImage(apiPath ?? null);
  const finalSrc = authed.url ?? (isUsableSrc(src) ? src! : null);
  const initials = initialsFor(name);

  if (finalSrc) {
    return (
      <img
        src={finalSrc}
        alt={name ?? 'Avatar'}
        width={size}
        height={size}
        style={{ width: size, height: size }}
        className={cn('rounded-full bg-app-surface-2 object-cover', className)}
      />
    );
  }

  return (
    <div
      style={{ width: size, height: size, fontSize: Math.max(11, Math.round(size * 0.36)) }}
      className={cn(
        'flex items-center justify-center rounded-full bg-primary/15 font-semibold text-primary',
        className,
      )}
      aria-label={name}
    >
      {initials}
    </div>
  );
}
